import React from "react";
import { NavLink } from "react-router-dom";
import { Eye, Type, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ShareButton from "@/components/ShareButton";
import ReadModeToggle from "@/components/ReadModeToggle";
import NightModeToggle from "@/components/NightModeToggle";
import SoundToggle from "@/components/SoundToggle";
import { HUBS } from "@/lib/navRegistry";
import { track } from "@/lib/track";

/**
 * DesktopDropdown — the "Menu" trigger + dropdown rendered inside the
 * desktop header on `lg+` viewports. Extracted from Layout.jsx during
 * Cycle 13.6 (Feb 2026) so the parent stays lean and this menu can be
 * iterated on independently.
 *
 * Purely presentational: every piece of state / gating / navigation
 * data is passed in from Layout so this file doesn't fork the source of
 * truth. Keeps parity with <MobileDrawer /> (same NAV_GROUPS, same
 * gating, same subjects accordion).
 */
export default function DesktopDropdown({
  secondaryActive,
  keyObjectives,
  navGroups,
  navGatedHide,
  buildItem,
  gateModeByKey,
  handleGrownupNavClick,
  collapseSubjects,
  subjectsExpanded,
  setSubjectsExpanded,
  subjectPreview,
  progress,
  toggle,
  ageMeta,
  openGate,
}) {
  // Feb 2026 · Cycle 62 fix — controlled open state so tapping
  // Parents & Teachers (or any utility button that triggers a
  // separate modal like the grown-up gate) closes the menu first.
  // Previously the ⋯ menu stayed open behind the gate modal, which
  // hid the answer input on narrow desktops and read as "the parents
  // link is broken".
  const [open, setOpen] = React.useState(false);
  const close = React.useCallback(() => setOpen(false), []);
  const { t } = useTranslation();
  // Cycle 22E · per-group expand/collapse state for groups that
  // declare `preview: N`. Kept as a local map so state is isolated
  // per group and doesn't need to travel up to Layout.

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="nav-more"
          aria-label="More options and menu"
          title="More"
          className={cn(
            "px-3 h-10 rounded-full font-bold text-sm border-[3px] border-slate-900 transition-all whitespace-nowrap inline-flex items-center gap-1.5 shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)]",
            secondaryActive
              ? "bg-slate-900 text-white"
              : "bg-white text-slate-900 hover:bg-yellow-100",
          )}
        >
          <MoreHorizontal size={18} strokeWidth={3} />
          <span>More</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="border-[3px] border-slate-900 rounded-xl shadow-[0_4px_0_0_rgba(15,23,42,1)] bg-white p-2 min-w-[340px] max-w-[420px] max-h-[80vh] overflow-y-auto"
      >
        {/* Feb 2026 · Menu top condensed from 3 rows to 2.
              Row 1 — one compact row of every quick control:
                Age · Sound · Language · Share · Home.
              Row 2 — Parents & Teachers (gated, deliberately separate).
              Removes the previous big yellow Home tile which duplicated
              the primary top-nav's Home link and added ~60 px of
              vertical space to every menu open. */}
        <div className="px-1 pb-2 mb-1 border-b border-slate-200" data-testid="menu-utility-row">
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="menu-quick-row">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("we-open-age-selector"))}
              data-testid="menu-age-chip"
              aria-label={ageMeta ? `Age tier: ${ageMeta.label}. Tap to change.` : "Pick age tier"}
              className={cn(
                "inline-flex items-center gap-1 h-10 px-3 rounded-full border-[2.5px] border-slate-900 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-all bg-gradient-to-b",
                ageMeta?.id === "little"
                  ? "from-rose-100 to-rose-200"
                  : ageMeta?.id === "big"
                    ? "from-yellow-100 to-amber-200"
                    : ageMeta?.id === "pro"
                      ? "from-emerald-100 to-emerald-200"
                      : "from-white to-yellow-50",
              )}
              title={ageMeta ? `${ageMeta.label} — tap to change` : "Pick age"}
            >
              <span aria-hidden>{ageMeta?.emoji || "👋"}</span>
              <span className="truncate max-w-[76px]">{ageMeta?.label || "Pick age"}</span>
            </button>
            <div
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border-[2.5px] border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)]"
              data-testid="menu-sound-toggle"
              title="Sound"
            >
              <SoundToggle size="sm" />
            </div>
            <div className="flex-1 min-w-[112px]">
              <LanguageSwitcher />
            </div>
            <ShareButton size="sm" variant="ghost" testId="menu-share-btn" />
          </div>

        </div>

        {/* 🎯 Today's Learning Goal — single line. */}
        {keyObjectives.length > 0 && (
          <div data-testid="nav-group-objectives" className="mb-1">
            {keyObjectives.map((obj) => (
              <DropdownMenuItem key={obj.outcomeCode} asChild>
                <NavLink
                  to={obj.to}
                  data-testid={`nav-objective-${obj.outcomeCode}`}
                  className="font-bold cursor-pointer rounded-lg px-3 py-2 text-sm flex items-center gap-2 w-full hover:bg-rose-50"
                >
                  <span aria-hidden>🎯</span>
                  <span className="flex-1 min-w-0 truncate">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-rose-800 mr-1">Today:</span>
                    {obj.shortTitle}
                  </span>
                </NavLink>
              </DropdownMenuItem>
            ))}
            <div className="my-1.5 border-t border-slate-200" aria-hidden />
          </div>
        )}

        {/* Jun 2026 · More = utility menu (Everything · Help · Grown-up Mode). Deep links live in hubs + footer. */}
        <div className="grid grid-cols-5 gap-1" data-testid="desktop-more-hubs">
          {HUBS.filter((h) => h.audience === "child").map((h) => (
            <DropdownMenuItem key={h.id} asChild>
              <NavLink to={h.to} end={h.to === "/"} onClick={() => track("nav_destination_selected", { hub: h.id, via: "more-desktop" })} data-testid={`desktop-more-hub-${h.id}`}
                className={({ isActive }) => cn("flex flex-col items-center gap-0.5 rounded-xl px-1 py-2 text-center text-[11px] font-extrabold", isActive ? "bg-[var(--color-primary-surface)] text-primary-safe" : "hover:bg-[var(--color-neutral-surface)]")}>
                <span aria-hidden className="text-xl leading-none">{h.emoji}</span>{h.label}
              </NavLink>
            </DropdownMenuItem>
          ))}
        </div>
        <div className="my-1.5 border-t border-slate-200" aria-hidden />
        {[{ id: "everything", label: "Everything", desc: "The whole catalogue", to: "/everything", emoji: "🔎" }, { id: "help", label: "Help", desc: "How MWQ works", to: "/help", emoji: "❓" }, { id: "grown-ups", label: "Grown-up Mode", desc: "Parents · Teachers · Schools", to: "/grown-ups", emoji: "🔒" }].map((u) => (
          <DropdownMenuItem key={u.id} asChild>
            <NavLink to={u.to} onClick={() => track("nav_destination_selected", { hub: u.id, via: "more-desktop" })} data-testid={`desktop-more-util-${u.id}`} className="flex items-center gap-3 min-h-[44px] px-2 rounded-lg hover:bg-[var(--color-neutral-surface)]">
              <span aria-hidden className="text-lg">{u.emoji}</span>
              <span className="min-w-0 flex-1"><span className="block text-sm font-extrabold">{u.label}</span><span className="block mwq-meta text-xs">{u.desc}</span></span>
            </NavLink>
          </DropdownMenuItem>
        ))}

        {/* Accessibility toggles — pinned to the bottom. */}
        <div className="mt-2 pt-3 border-t-2 border-dashed border-slate-300 flex items-center flex-wrap gap-2">
          <ReadModeToggle variant="chip" />
          <NightModeToggle compact />
          <button
            type="button"
            onClick={() => toggle("highContrast")}
            data-testid="desktop-toggle-contrast"
            title={t("ui.accessibility.highContrast")}
            aria-pressed={progress.settings.highContrast}
            className={cn(
              "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-[2.5px] border-slate-900 font-bold text-xs",
              progress.settings.highContrast ? "bg-slate-900 text-white" : "bg-white text-slate-900",
            )}
          >
            <Eye size={14} strokeWidth={3} />
            {t("ui.accessibility.highContrast")}
          </button>
          <button
            type="button"
            onClick={() => toggle("bigText")}
            data-testid="desktop-toggle-bigtext"
            title={t("ui.accessibility.bigText")}
            aria-pressed={progress.settings.bigText}
            className={cn(
              "inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-[2.5px] border-slate-900 font-bold text-xs",
              progress.settings.bigText ? "bg-slate-900 text-white" : "bg-white text-slate-900",
            )}
          >
            <Type size={14} strokeWidth={3} />
            {t("ui.accessibility.bigText")}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
